import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  endSession,
  getAuthToken,
  getSessionTimeLeftMs,
  hasActiveSession,
  subscribeToSessionChanges,
} from "@/utility/authSession";

/** Safety net so an idle tab still notices expiry without any user action. */
const EXPIRY_POLL_MS = 30000;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // A token whose `exp` has passed is no longer a session: the router must send
  // the user to login instead of mounting the protected page and bouncing on 401.
  const [isAuthenticated, setIsAuthenticated] = useState(hasActiveSession);

  const sync = useCallback(() => {
    const token = getAuthToken();
    const active = hasActiveSession();
    // Remove an expired token/session id instead of leaving dead credentials
    // available to API helpers or newly opened tabs. Local only: observing an
    // expiry is not a sign-out order for the other tabs, and `expectedToken`
    // makes the clear a no-op if a fresh login landed in the meantime.
    if (!active && token) {
      endSession({
        reason: "expired",
        notifyLocal: false,
        broadcast: false,
        expectedToken: token,
      });
    }
    setIsAuthenticated((current) => {
      return active === current ? current : active;
    });
  }, []);

  useEffect(() => {
    // Re-checks on tab focus, tab visibility and sign-out from another tab.
    sync();
    return subscribeToSessionChanges(sync);
  }, [sync]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const timers = [setInterval(sync, EXPIRY_POLL_MS)];

    // Flip exactly when the token expires instead of waiting for the poll.
    const timeLeft = getSessionTimeLeftMs();
    if (timeLeft != null && timeLeft > 0 && timeLeft < 2 ** 31 - 1) {
      timers.push(setTimeout(sync, timeLeft));
    }

    return () => {
      clearInterval(timers[0]);
      if (timers[1]) clearTimeout(timers[1]);
    };
  }, [isAuthenticated, sync]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, setIsAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
